/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from "react";
import { Col, Input, Row } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-number-input";
import es from "react-phone-number-input/locale/es";
import useStores from "graphql/useStores";
import { useFormik } from "formik";
import useStoreMutations from "graphql/useStoreMutations";
import Loading from "components/common/loading";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import s from "../../styles/Settings.styles";
import { useBusinessContext } from "../../BusinessApp";

function StoreSettings() {
  const { t } = useTranslation();

  const { stores, loading: loading1 } = useStores();

  const { storeInfo } = useBusinessContext();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const { updateStore } = useStoreMutations();

  const { setInfoMessage, setErrorMessage } = useMessaging();

  const {
    values,
    getFieldProps,
    handleSubmit,
    resetForm,
    isValid,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: storeInfo || {
      ...storeInfo,
    },
    onSubmit: async (values) => {
      const newValues = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        address1: values.address1,
        city: values.city,
        zip: values.zip,
        country: values.country,
        webPage: values.webPage,
      };
      // eslint-disable-next-line no-unused-vars
      const { _id, ...updateValues } = newValues;
      try {
        setEditing(true);
        setSaving(true);
        await updateStore(storeInfo || stores[0], updateValues);
        setSaving(false);
        setEditing(false);
        onUpdateSuccess();
      } catch (error) {
        setErrorMessage({ error });
        setEditing(false);
      }
    },
  });

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.SettingsStoreSettings.infoEdit"),
      info1: t("containers.SettingsStoreSettings.infoEditDB"),
    });
  }, []);

  if (loading1) return <Loading />;

  return (
    <>
      <s.container>
        <Row>
          <Col>
            <s.storeHours>
              {t("containers.SettingsStoreSettings.settingsStore")}
              <s.storeHoursSpan>
                {storeInfo?.address1 && ` (${storeInfo?.address1})`}
              </s.storeHoursSpan>
            </s.storeHours>
            <s.message>
              {storeInfo
                ? t("containers.SettingsStoreSettings.edditStore")
                : t("containers.SettingsStoreSettings.selectStore")}
            </s.message>
          </Col>
        </Row>
      </s.container>

      <s.containerBody>
        <s.SectionTitle style={{ marginBottom: "18px", marginTop: "40px" }}>
          {t("containers.SettingsStoreSettings.informationGeneral")}
        </s.SectionTitle>

        <s.Line style={{ marginBottom: "10px" }} />

        <Row>
          <Col span={24}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.name")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={values.name}
              {...getFieldProps("name")}
            />
          </Col>
        </Row>

        <Row gutter={9} style={{ marginTop: "27px" }}>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.email")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={values.email}
              {...getFieldProps("email")}
            />
          </Col>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.phone")}
            </s.SubTitle>
            <PhoneInput
              style={{
                border: "1px solid #d9d9d9",
                height: "31.6px",
              }}
              labels={es}
              limitMaxLength={true}
              disabled={!editing}
              international={false}
              placeholder={t("general.textPlaceholder")}
              defaultCountry="ES"
              value={values.phone}
              onChange={(value) => [
                setFieldValue("phone", value),
                setFieldTouched("phone", value),
              ]}
            />
          </Col>
        </Row>

        <s.SectionTitle style={{ marginBottom: "18px", marginTop: "68px" }}>
          {t("containers.SettingsStoreSettings.companyAddress")}
        </s.SectionTitle>
        <s.Line style={{ marginBottom: "10px" }} />

        <Row gutter={9}>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.streetAndNumber")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={values.address1}
              {...getFieldProps("address1")}
            />
          </Col>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.city")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              city={values.city}
              {...getFieldProps("city")}
            />
          </Col>
        </Row>

        <Row gutter={9} style={{ marginTop: "27px" }}>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.postalCode")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={values.zip}
              {...getFieldProps("zip")}
            />
          </Col>
          <Col span={12}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.country")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={values.country}
              {...getFieldProps("country")}
            />
          </Col>
        </Row>

        <Row>
          <Col span={24} style={{ marginTop: "27px" }}>
            <s.SubTitle>
              {t("containers.SettingsStoreSettings.website")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              {...getFieldProps("webPage")}
            />
          </Col>
        </Row>

        {!editing ? (
          <s.InputContainer style={{ marginTop: "47px" }}>
            <s.button disabled={!storeInfo} onClick={() => setEditing(true)}>
              {t("containers.SettingsStoreSettings.edit")}
            </s.button>
          </s.InputContainer>
        ) : (
          <s.containerButton style={{ marginTop: "47px" }}>
            <s.InputContainer style={{ marginRight: "15px" }}>
              <s.button
                disabled={false}
                onClick={() => [setEditing(false), resetForm()]}
              >
                {t("containers.SettingsStoreSettings.return")}
              </s.button>
            </s.InputContainer>
            <s.InputContainer>
              <s.buttonsave
                disabled={isValid && dirty}
                onClick={() => handleSubmit()}
              >
                {t("containers.SettingsStoreSettings.save")}
                {saving && (
                  <LoadingOutlined
                    style={{
                      fontSize: 24,
                      marginLeft: "15px",
                    }}
                    spin
                  />
                )}
              </s.buttonsave>
            </s.InputContainer>
          </s.containerButton>
        )}
      </s.containerBody>
    </>
  );
}

export default StoreSettings;
